import React from 'react';
import './ClickerArt.css';

// Clompy Clicker's drawings: the mugs, the diamond, the coffee machine, the cold brew tower, the Thermos and the
// backgrounds. They are drawn here in code rather than kept as picture files, so the game can recolour and animate
// them, and the game's trading cards (TradingCard.jsx) can show the very same drawings.

// How each mug the shop sells is coloured. The To-Go Cup (mug_togo) has a drawing of its own: see ToGoCup.
const MUGS = {
    mug_classic: { body: '#b0e0e6', shade: '#8fcdd6', light: '#e6fbfd', stripe: '#ff7a1a', outline: '#16222e', face: '#16222e', cheeks: '#ff9d8a' },
    mug_alien: { body: '#62e06a', shade: '#43b84c', light: '#d0ffd4', stripe: '#2d7a35', outline: '#12301a', face: '#12301a', cheeks: '#b6ff6a' },
    mug_gold: { body: '#ffd56a', shade: '#e0a93a', light: '#fff4c8', stripe: '#b07e1a', outline: '#4a3208', face: '#4a3208', cheeks: '#ff9d6a' }
};

// A cut diamond seen from the side: a flat top, a row of facets above the widest point and three running down to
// the tip. `golden` draws the golden diamond that drifts across the game.
export function DiamondIcon({ golden }) {
    let c = golden ?
        ['#ffe9a8', '#fff8dc', '#ffd56a', '#ffeebb', '#f3bb3c', '#e0a93a', '#ffcf4d', '#b07e1a', '#7a5410']
        : ['#8fe3ff', '#d4f6ff', '#6fd6fb', '#a9ebff', '#4fb9ea', '#35a7e0', '#57c8f5', '#2386c8', '#1b6fa8'];
    return (
        <svg className="clompy-diamond" viewBox="0 0 24 24" aria-hidden="true">
            <polygon points="6,3 2,9 8,9" fill={c[0]} />
            <polygon points="6,3 12,3 8,9" fill={c[1]} />
            <polygon points="12,3 8,9 16,9" fill={c[2]} />
            <polygon points="12,3 18,3 16,9" fill={c[3]} />
            <polygon points="18,3 22,9 16,9" fill={c[4]} />
            <polygon points="2,9 8,9 12,22" fill={c[5]} />
            <polygon points="8,9 16,9 12,22" fill={c[6]} />
            <polygon points="16,9 22,9 12,22" fill={c[7]} />
            <polygon points="6,3 18,3 22,9 12,22 2,9" fill="none" stroke={c[8]} strokeWidth="1" strokeLinejoin="round" />
        </svg>
    );
}

// Clompy as a paper takeaway cup: a tapered cup with a lid, a card sleeve with the face on it, and no handle.
// Drawn in the same box as the mug, standing on the same spot. Takes what ClompyMug takes.
function ToGoCup({ still, ...rest }) {
    let clip = `clompy-togo-body${still ? '-still' : ''}`;
    let outline = '#16222e';
    let cup = 'M68 86l22 162a16 16 0 0 0 16 14h74a16 16 0 0 0 16-14l22-162z';
    return (
        <svg viewBox="0 0 320 300" {...rest}>
            <defs>
                <clipPath id={clip}><path d={cup} /></clipPath>
            </defs>
            {still ? null : <path className="clompy-steam" d="M186 30c-10-9 10-15 0-26" />}
            {/* the cup, darker down its right side */}
            <path d={cup} fill="#f6eedd" />
            <g clipPath={`url(#${clip})`}>
                <rect x="176" y="80" width="50" height="190" fill="#e0d3b8" />
                <rect x="60" y="236" width="170" height="10" fill="powderblue" />
            </g>
            <path d={cup} fill="none" stroke={outline} strokeWidth="9" strokeLinejoin="round" />
            {/* the card sleeve */}
            <path d="M70 132h146l-11 86H81z" fill="#c98a4b" />
            <path d="M190 132h26l-11 86h-22z" fill="#a86f38" />
            <path d="M70 132h146l-11 86H81z" fill="none" stroke={outline} strokeWidth="8" strokeLinejoin="round" />
            {/* the lid: a rim, a dome and the hole to drink from */}
            <path d="M80 66q2-24 22-24h82q20 0 22 24z" fill="#ffffff" stroke={outline} strokeWidth="9" strokeLinejoin="round" />
            <rect x="56" y="64" width="174" height="28" rx="12" fill="#ffffff" stroke={outline} strokeWidth="9" />
            <rect x="196" y="68" width="30" height="20" rx="8" fill="#dfe6ea" />
            <ellipse cx="186" cy="50" rx="13" ry="5" fill={outline} />
            <path d="M96 56q4-8 14-8" fill="none" stroke="#dfe6ea" strokeWidth="5" strokeLinecap="round" />
            {/* the face, on the sleeve */}
            <ellipse cx="116" cy="166" rx="11" ry="15" fill={outline} />
            <ellipse cx="170" cy="166" rx="11" ry="15" fill={outline} />
            <circle cx="120" cy="160" r="4.5" fill="#ffffff" />
            <circle cx="174" cy="160" r="4.5" fill="#ffffff" />
            <ellipse cx="98" cy="190" rx="10" ry="6" fill="#ff9d8a" opacity="0.8" />
            <ellipse cx="188" cy="190" rx="10" ry="6" fill="#ff9d8a" opacity="0.8" />
            <path d="M128 188q15 16 30 0" fill="none" stroke={outline} strokeWidth="7" strokeLinecap="round" />
        </svg>
    );
}

// Clompy the mug, drawn as a game character: thick outline, flat shading, a face and rising steam.
// `skin` is the shop's id for the mug to draw; `still` leaves the steam out (the shop's small pictures).
// Anything else (the click handlers) goes on the drawing itself.
export function ClompyMug({ skin, still, ...rest }) {
    if (skin === 'mug_togo') {
        return <ToGoCup still={still} {...rest} />;
    }
    let c = MUGS[skin] || MUGS.mug_classic;
    let clip = `clompy-mug-body-${skin}${still ? '-still' : ''}`;
    let body = 'M48 74v156a32 32 0 0 0 32 32h126a32 32 0 0 0 32-32V74z';
    let handle = 'M232 112h22a50 50 0 0 1 0 100h-22';
    return (
        <svg viewBox="0 0 320 300" {...rest}>
            <defs>
                <clipPath id={clip}><path d={body} /></clipPath>
            </defs>
            {still ? null :
                <>
                    <path className="clompy-steam" d="M104 40c-10-9 10-15 0-26" />
                    <path className="clompy-steam clompy-steam-2" d="M143 34c-10-9 10-15 0-26" />
                    <path className="clompy-steam clompy-steam-3" d="M182 40c-10-9 10-15 0-26" />
                </>
            }
            <path d={handle} fill="none" stroke={c.outline} strokeWidth="40" strokeLinecap="round" />
            <path d={handle} fill="none" stroke={c.shade} strokeWidth="24" strokeLinecap="round" />
            <path d="M236 105h18a57 57 0 0 1 40 17" fill="none" stroke={c.light} strokeWidth="6" strokeLinecap="round" />
            <path d={body} fill={c.body} />
            <g clipPath={`url(#${clip})`}>
                <rect x="186" y="60" width="60" height="210" fill={c.shade} />
                <rect x="48" y="226" width="190" height="40" fill={c.shade} opacity="0.7" />
                <rect x="64" y="96" width="14" height="96" rx="7" fill={c.light} />
                <rect x="64" y="204" width="14" height="16" rx="7" fill={c.light} />
                <rect x="48" y="232" width="190" height="12" fill={c.stripe} />
            </g>
            <path d={body} fill="none" stroke={c.outline} strokeWidth="9" strokeLinejoin="round" />
            <ellipse cx="143" cy="74" rx="95" ry="25" fill={c.light} stroke={c.outline} strokeWidth="9" />
            <ellipse cx="143" cy="77" rx="78" ry="16" fill="#6b3f24" />
            <path d="M82 76a62 12 0 0 1 60-9" fill="none" stroke="#a8683f" strokeWidth="5" strokeLinecap="round" />
            <ellipse cx="108" cy="158" rx="12" ry="16" fill={c.face} />
            <ellipse cx="170" cy="158" rx="12" ry="16" fill={c.face} />
            <circle cx="112" cy="151" r="5" fill="#ffffff" />
            <circle cx="174" cy="151" r="5" fill="#ffffff" />
            <ellipse cx="88" cy="184" rx="11" ry="7" fill={c.cheeks} opacity="0.75" />
            <ellipse cx="190" cy="184" rx="11" ry="7" fill={c.cheeks} opacity="0.75" />
            <path d="M122 182q17 18 34 0" fill="none" stroke={c.face} strokeWidth="7" strokeLinecap="round" />
        </svg>
    );
}

// Which of its looks a Coffee Machine or Cold Brew Tower level has: a new one at level 1 and at every fifth level
// (1-4, 5-9, 10-14, 15-19, 20-24, 25), or 0 before the first is bought
export function machineTier(level) {
    return level < 1 ? 0 : Math.min(6, Math.floor(level / 5) + 1);
}

// How the espresso machines (looks 3 to 6) are coloured: steel, red, black and gold, then diamond
const ESPRESSO = {
    3: { body: '#c9d1d9', shade: '#9aa7b5', trim: '#5a6676', light: '#f2f5f8' },
    4: { body: '#d9463f', shade: '#a8322c', trim: '#e9eef2', light: '#ff8a80' },
    5: { body: '#2b2b31', shade: '#1b1b20', trim: '#ffd56a', light: '#5b5b66' },
    6: { body: '#6fd6fb', shade: '#35a7e0', trim: '#d4f6ff', light: '#d4f6ff' }
};

// The coffee machine that stands beside the mug once the Coffee Machine upgrade has been bought, in look `tier`:
//   1 a small drip brewer; 2 the same with a water tank and a light; 3 a steel espresso machine with a gauge;
//   4 a red one with two spouts and a steam wand; 5 black and gold with a bean hopper and lights;
//   6 cut from diamond, with a diamond on top
// `still` leaves out the rising steam (the trading cards' pictures).
export function CoffeeMachine({ tier, still, ...rest }) {
    let outline = '#16222e';
    let line = { stroke: outline, strokeWidth: 6, strokeLinejoin: 'round', strokeLinecap: 'round' };
    let cup = (x) => (
        <g>
            <path d={`M${x} 190h22l-3 16h-16z`} fill="#ffffff" {...line} strokeWidth="4" />
            <rect x={x + 9} y="176" width="4" height="12" rx="2" fill="#6b3f24" />
        </g>
    );
    if (tier <= 2) {
        let body = tier === 2 ? '#4f7fa8' : '#8a8f98';
        let shade = tier === 2 ? '#3b6385' : '#6c7079';
        return (
            <svg viewBox="0 0 200 240" {...rest}>
                {still ? null : <path className="clompy-steam" d="M82 118c-8-7 8-12 0-21" />}
                {tier === 2 ?
                    <>
                        <rect x="116" y="26" width="42" height="40" rx="6" fill="#bfe9f5" opacity="0.85" {...line} />
                        <rect x="119" y="42" width="36" height="22" rx="3" fill="#6fc3e0" />
                    </>
                    : null}
                {/* the column at the back, the head that drips, and the plate the pot stands on */}
                <rect x="110" y="70" width="50" height="146" fill={shade} {...line} />
                <rect x="38" y="60" width="124" height="40" rx="9" fill={body} {...line} />
                <rect x="76" y="100" width="12" height="12" fill={shade} {...line} strokeWidth="4" />
                <rect x="34" y="210" width="132" height="22" rx="7" fill={body} {...line} />
                {/* the glass pot, half full */}
                <path d="M54 130h56v64a14 14 0 0 1-14 14H68a14 14 0 0 1-14-14z" fill="#dff3f7" opacity="0.9" />
                <path d="M54 164h56v30a14 14 0 0 1-14 14H68a14 14 0 0 1-14-14z" fill="#6b3f24" />
                <path d="M54 130h56v64a14 14 0 0 1-14 14H68a14 14 0 0 1-14-14z" fill="none" {...line} />
                <path d="M54 142h-8a8 8 0 0 0-8 8v22a8 8 0 0 0 8 8h8" fill="none" {...line} />
                <rect x="50" y="124" width="64" height="10" rx="4" fill={outline} />
                {tier === 2 ? <circle cx="135" cy="118" r="7" fill="#62e06a" {...line} strokeWidth="4" /> : null}
                <rect x="124" y="140" width="22" height="8" rx="4" fill={outline} opacity="0.5" />
            </svg>
        );
    }
    let c = ESPRESSO[tier];
    return (
        <svg viewBox="0 0 200 240" {...rest}>
            {still ? null : <path className="clompy-steam" d={tier >= 4 ? 'M170 150c-8-7 8-12 0-21' : 'M82 168c-8-7 8-12 0-21'} />}
            {tier === 5 ?
                <>
                    {/* the hopper, full of beans */}
                    <path d="M66 18h68l-10 42H76z" fill="#dff3f7" opacity="0.85" {...line} />
                    <path d="M71 38h58l-5 22H76z" fill="#6b3f24" />
                    <rect x="60" y="10" width="80" height="12" rx="5" fill={c.trim} {...line} />
                </>
                : null}
            {tier === 6 ?
                <g className="clompy-machine-gem">
                    <polygon points="84,14 116,14 130,32 100,62 70,32" fill="#d4f6ff" {...line} />
                    <polygon points="84,14 100,32 116,14" fill="#ffffff" />
                    <polygon points="70,32 130,32 100,62" fill="#8fe3ff" />
                    <polygon points="84,14 116,14 130,32 100,62 70,32" fill="none" {...line} />
                </g>
                : null}
            {/* the feet and drip tray, the dark space the cups stand in, and the body above it */}
            <rect x="22" y="212" width="156" height="20" rx="7" fill={c.trim} {...line} />
            <rect x="30" y="172" width="140" height="42" fill="#20262e" {...line} />
            <rect x="34" y="204" width="132" height="8" rx="3" fill={outline} opacity="0.6" />
            <rect x="18" y="70" width="164" height="108" rx="13" fill={c.body} {...line} />
            <rect x="136" y="73" width="43" height="102" rx="10" fill={c.shade} />
            <rect x="30" y="84" width="10" height="56" rx="5" fill={c.light} opacity="0.8" />
            <rect x="18" y="70" width="164" height="108" rx="13" fill="none" {...line} />
            <rect x="12" y="56" width="176" height="22" rx="8" fill={c.trim} {...line} />
            {tier === 6 ?
                <>
                    <polygon points="52,96 64,84 76,96 64,116" fill="#d4f6ff" opacity="0.9" />
                    <polygon points="118,128 128,118 138,128 128,146" fill="#d4f6ff" opacity="0.9" />
                </>
                : null}
            {/* the pressure gauge, and from the black machine up a row of lights */}
            <circle cx="100" cy="116" r="19" fill="#ffffff" {...line} />
            <path d={tier >= 5 ? 'M100 116l12-9' : 'M100 116l-9-10'} fill="none" stroke="#d9463f" strokeWidth="4" strokeLinecap="round" />
            <circle cx="100" cy="116" r="3" fill={outline} />
            {tier >= 5 ?
                <>
                    <circle cx="52" cy="150" r="6" fill="#62e06a" {...line} strokeWidth="3" />
                    <circle cx="72" cy="150" r="6" fill="#ffd56a" {...line} strokeWidth="3" />
                    <circle cx="148" cy="150" r="6" fill="#ff7a1a" {...line} strokeWidth="3" />
                </>
                : null}
            {/* a spout with its handle and a cup under it; two from the red machine up, with a steam wand */}
            <rect x={tier >= 4 ? 50 : 76} y="172" width="32" height="12" rx="3" fill={c.trim} {...line} strokeWidth="4" />
            {cup(tier >= 4 ? 55 : 81)}
            {tier >= 4 ?
                <>
                    <rect x="104" y="172" width="32" height="12" rx="3" fill={c.trim} {...line} strokeWidth="4" />
                    {cup(109)}
                    <path d="M176 150q10 4 6 24l-6 22" fill="none" {...line} />
                    <path d="M176 150q10 4 6 24l-6 22" fill="none" stroke={c.trim} strokeWidth="2" strokeLinecap="round" />
                </>
                : null}
        </svg>
    );
}

// How the cold brew tower's frame and fittings are coloured in each of its looks: pine, oak, walnut with brass,
// steel, black and gold, then diamond
const TOWERS = {
    1: { post: '#d9a066', shade: '#c08a52', plank: '#e6b983', tap: '#5a6676' },
    2: { post: '#b9793f', shade: '#9a5f2e', plank: '#c98a4b', tap: '#5a6676' },
    3: { post: '#6b3f24', shade: '#4a2a18', plank: '#8a5230', tap: '#e0aa3e' },
    4: { post: '#c9d1d9', shade: '#9aa7b5', plank: '#e9eef2', tap: '#5a6676' },
    5: { post: '#2b2b31', shade: '#1b1b20', plank: '#ffd56a', tap: '#ffd56a' },
    6: { post: '#6fd6fb', shade: '#35a7e0', plank: '#d4f6ff', tap: '#d4f6ff' }
};

// The cold brew tower that stands on the other side of the mug once that upgrade has been bought (the backend
// stores it as `barista`): a frame holding a globe of iced water, which drips down into a jar. It is what earns
// diamonds while the player is away. Like the coffee machine it has a look for each `tier` (see machineTier):
//   1 a pine frame, the globe dripping straight into the jar; 2 oak, with a chamber of coffee grounds on a shelf;
//   3 walnut with a brass tap and a glass coil; 4 steel, with a valve wheel and a measuring jar;
//   5 black and gold with lights; 6 cut from diamond, with a diamond in the globe in place of the ice
// `still` leaves out the falling drop (the trading cards' pictures).
export function ColdBrewTower({ tier, still, ...rest }) {
    let c = TOWERS[tier] || TOWERS[1];
    let outline = '#16222e';
    let line = { stroke: outline, strokeWidth: 6, strokeLinejoin: 'round', strokeLinecap: 'round' };
    return (
        <svg viewBox="0 0 200 240" {...rest}>
            {/* the frame: two posts, a top and a base, and from the second look a shelf */}
            <rect x="36" y="26" width="14" height="196" fill={c.post} {...line} />
            <rect x="150" y="26" width="14" height="196" fill={c.shade} {...line} />
            <rect x="26" y="16" width="148" height="16" rx="5" fill={c.plank} {...line} />
            <rect x="26" y="216" width="148" height="16" rx="5" fill={c.plank} {...line} />
            {tier >= 2 ? <rect x="46" y="118" width="108" height="10" fill={c.shade} {...line} strokeWidth="4" /> : null}
            {tier >= 5 ?
                <>
                    <circle cx="48" cy="224" r="4" fill="#62e06a" />
                    <circle cx="62" cy="224" r="4" fill={tier === 6 ? '#ffffff' : '#ff7a1a'} />
                    <circle cx="152" cy="224" r="4" fill="#62e06a" />
                </>
                : null}
            {/* the globe of iced water (or, in the last look, of water round a diamond), and the tap under it */}
            <circle cx="100" cy="72" r="34" fill="#e9f8fb" opacity="0.9" />
            <path d="M67 80a34 34 0 0 0 66 0z" fill="#8fd3ee" />
            {tier === 6 ?
                <g className="clompy-machine-gem">
                    <polygon points="90,56 110,56 120,68 100,92 80,68" fill="#d4f6ff" stroke="#1b6fa8" strokeWidth="3" strokeLinejoin="round" />
                    <polygon points="90,56 100,68 110,56" fill="#ffffff" />
                    <polygon points="80,68 120,68 100,92" fill="#6fd6fb" />
                </g>
                :
                <>
                    <rect x="80" y="64" width="14" height="14" rx="3" fill="#ffffff" stroke="#bfe9f5" strokeWidth="2" transform="rotate(-12 87 71)" />
                    <rect x="104" y="68" width="13" height="13" rx="3" fill="#ffffff" stroke="#bfe9f5" strokeWidth="2" transform="rotate(14 110 74)" />
                </>
            }
            <circle cx="100" cy="72" r="34" fill="none" {...line} />
            <path d="M78 54a26 26 0 0 1 16-10" fill="none" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" />
            <rect x="94" y="104" width="12" height="16" fill={c.tap} {...line} strokeWidth="4" />
            {tier >= 4 ?
                <>
                    {/* a wheel to set the drip */}
                    <path d="M106 112h14" fill="none" {...line} strokeWidth="4" />
                    <circle cx="126" cy="112" r="8" fill={tier === 4 ? '#d9463f' : c.tap} {...line} strokeWidth="4" />
                </>
                : null}
            {tier >= 2 ?
                <>
                    {/* the chamber of grounds */}
                    <rect x="76" y="130" width="48" height="36" rx="8" fill="#e9f8fb" opacity="0.9" />
                    <rect x="79" y="142" width="42" height="21" rx="5" fill="#4a2a18" />
                    <rect x="76" y="130" width="48" height="36" rx="8" fill="none" {...line} />
                </>
                :
                <path d="M100 120v52" fill="none" {...line} strokeWidth="4" />
            }
            {/* down to the jar: a straight pipe, or from the third look a glass coil */}
            {tier >= 3 ?
                <>
                    <path d="M100 166q14 3 0 7q-14 3 0 7q14 3 0 6" fill="none" stroke={outline} strokeWidth="7" strokeLinecap="round" />
                    <path d="M100 166q14 3 0 7q-14 3 0 7q14 3 0 6" fill="none" stroke="#bfe9f5" strokeWidth="3" strokeLinecap="round" />
                </>
                : tier === 2 ? <path d="M100 166v10" fill="none" {...line} strokeWidth="4" /> : null}
            {tier >= 3 || still ? null : <circle className="clompy-drip" cx="100" cy="178" r="4" fill="#6b3f24" />}
            <path d="M70 186h60v18a12 12 0 0 1-12 12H82a12 12 0 0 1-12-12z" fill="#e9f8fb" opacity="0.9" />
            <path d={tier >= 4 ? 'M70 192h60v12a12 12 0 0 1-12 12H82a12 12 0 0 1-12-12z' : 'M70 198h60v6a12 12 0 0 1-12 12H82a12 12 0 0 1-12-12z'} fill="#6b3f24" />
            <path d="M70 186h60v18a12 12 0 0 1-12 12H82a12 12 0 0 1-12-12z" fill="none" {...line} />
            {tier >= 4 ? <path d="M122 194h-7M122 202h-7M122 210h-7" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.8" /> : null}
        </svg>
    );
}

// The Thermos: a flask in Clompy's blue with a steel cup for a lid and the game's diamond on its side. The game
// itself has no picture of it; this one is for its trading card.
export function Thermos(props) {
    let outline = '#16222e';
    let line = { stroke: outline, strokeWidth: 6, strokeLinejoin: 'round', strokeLinecap: 'round' };
    return (
        <svg viewBox="0 0 200 240" {...props}>
            {/* the handle */}
            <path d="M140 104h14a12 12 0 0 1 12 12v52a12 12 0 0 1-12 12h-14" fill="none" stroke={outline} strokeWidth="18" strokeLinecap="round" />
            <path d="M140 104h14a12 12 0 0 1 12 12v52a12 12 0 0 1-12 12h-14" fill="none" stroke="#9aa7b5" strokeWidth="8" strokeLinecap="round" />
            {/* the flask, darker down its right side, with steel bands */}
            <rect x="58" y="76" width="84" height="150" rx="16" fill="#b0e0e6" />
            <rect x="116" y="76" width="26" height="150" rx="12" fill="#8fcdd6" />
            <rect x="70" y="96" width="10" height="84" rx="5" fill="#e6fbfd" />
            <rect x="58" y="76" width="84" height="16" fill="#c9d1d9" />
            <rect x="58" y="200" width="84" height="12" fill="#ff7a1a" />
            <rect x="58" y="76" width="84" height="150" rx="16" fill="none" {...line} />
            {/* the neck and the cup that screws on over it */}
            <rect x="72" y="56" width="56" height="24" fill="#9aa7b5" {...line} />
            <path d="M64 22h72v26a10 10 0 0 1-10 10H74a10 10 0 0 1-10-10z" fill="#e9eef2" {...line} />
            <path d="M112 22h24v26a10 10 0 0 1-10 10h-14z" fill="#c9d1d9" />
            <path d="M64 22h72v26a10 10 0 0 1-10 10H74a10 10 0 0 1-10-10z" fill="none" {...line} />
            {/* the diamond on its side */}
            <polygon points="90,128 110,128 120,140 100,164 80,140" fill="#6fd6fb" stroke="#1b6fa8" strokeWidth="3" strokeLinejoin="round" />
            <polygon points="90,128 100,140 110,128" fill="#d4f6ff" />
            <polygon points="80,140 120,140 100,164" fill="#35a7e0" />
            <polygon points="90,128 110,128 120,140 100,164 80,140" fill="none" stroke="#1b6fa8" strokeWidth="3" strokeLinejoin="round" />
        </svg>
    );
}

// One of the game's backgrounds, filling whatever box it is put in: its sky, and the counter along the bottom.
// `background` is the shop's id for it (bg_night, bg_cafe, bg_sunrise, bg_space).
export function ClickerScene({ background }) {
    return <div className={`clicker-scene clompy-${background}`} aria-hidden="true"></div>;
}
